import React, { useState } from 'react';
import AuctionCard from '../auction-card/index';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { parseLocationSearchParams } from '../../../utils/helpers/parse-location-search-params';
import Carousel from '../../common/carousel';
import { Container, Paper, Alert } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Pagination, Thumbs } from 'swiper';
import InspectionReport from '../inspection-report/index';
import BidBottomWrap from '../bid/bid-bottom-wrap';
import BasicModal, { ConfirmationDialog } from '../../common/modal';
import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import SkeletonLoader from '../../common/skeleton-loader';
import BidModal from '../bid/index';
import CarImageModel from '../car-image/index';
import UpdateAppModal from '../../common/update-app-modal';
import { AUCTION_TYPE, NUMBER, TOAST_OPTIONS } from '../../../constants/app-constants';
import { isValidObject } from '../../../utils/is-valid-object';
import NoDataFound from '../../../pages/no-data-found';
import { isTimePassed } from '../../../utils/helpers/secondsToHMS';
import { ROUTE_PATH } from '../../../constants/route-constants';
import 'swiper/css/effect-cards';
import { priceFormatter } from '../../../utils/helpers/price-formatter';
import { isValidDealer } from '../../../utils/is-valid-dealer';
import { prepareGADataAndSendEvent } from '../../../tracking';

const AuctionDetail = ({ auctionDetail: auction, user, getAuctionDetailsConnect, submitPlaceBidConnect, setupToastConnect, setActiveSlideIndexConnect }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { step } = useParams();
    const [isBidOpen, setIsBidOpen] = useState(false);
    const [isOcbBid, setIsOcbBid] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isApiError, setIsApiError] = useState(false);
    const [isCarImageOpen, setIsCarImageOpen] = useState(false);
    const [activeCarImagesSection, setActiveCarImagesSection] = useState('');
    const { aid, view = '' } = parseLocationSearchParams(location.search);
    const { auctionId, images, auctionEndTimestamp, currentDealerBid, highestBidForCurrentDealer, auctionType, pai, activeSlideIndex, ocbV2, thresholdBid } = auction || {};
    const [thumbsSwiper, setThumbsSwiper] = useState(null);
    const { status } = user;
    const ref = useRef();
    useEffect(() => {
        fetchAuctionDetails();
        ref.current = setInterval(() => {
            fetchAuctionDetails(false);
        }, NUMBER.FIFTEEN_THOUSAND);
        return () => {
            clearInterval(ref.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const settings = {
        slidesPerView: 1,
        grabCursor: true,
        autoplay: false,
        pagination: {
            clickable: true,
            dynamicBullets: true
        },
        modules: [Pagination, Thumbs],
        thumbs: { swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null },
    };

    const fetchAuctionDetails = (isLoader = true) => {
        if (isLoader) setLoading(true);
        if (isApiError) setIsApiError(false);
        getAuctionDetailsConnect(aid).then(res => {
            if (isLoader) prepareGADataAndSendEvent('pdpPageLoad', user, res?.detail, 'Inspection report');
        }).catch(() => {
            setIsApiError(true);
        }).finally(() => setLoading(false));
    };

    const isOcb = auctionType === AUCTION_TYPE.OCB || auctionType === AUCTION_TYPE.PAI;
    const handleBack = () => {
        navigate(`/${step}${isOcb ? '?ocb=1' : ''}`);
    };

    const eligibleForBid = isValidDealer(status);
    const handlePlaceBid = (data) => {
        const { isLiveAndOcb } = data || {};
        if (!eligibleForBid) return;
        prepareGADataAndSendEvent('placeBid', user, auction, 'Detail page');
        if (auctionType === AUCTION_TYPE.PAI || (auctionType === AUCTION_TYPE.OCB && !ocbV2) || isLiveAndOcb) {
            setIsOcbBid(true);
            return;
        }
        setIsBidOpen(true);
    };

    const handleBuyCar = () => {
        const payload = { auctionId, amount: thresholdBid };
        setLoading(true);
        submitPlaceBidConnect(payload).then(() => {
            getAuctionDetailsConnect(aid);
            setupToastConnect({ message: 'Bid placed successfully!', type: TOAST_OPTIONS.SUCCESS });
            setIsOcbBid(false);
        }).catch((error) => {
            setupToastConnect({ message: error?.errorMessage || 'Bid place failed due to some internal error', type: TOAST_OPTIONS.ERROR });
        }).finally(() => setLoading(false));
    };

    const handleBidModalClose = () => {
        setIsBidOpen(false);
        fetchAuctionDetails(false);
    };

    const handleImageOpen = (eventType = '', item) => {
        const eventName = item?.total ? 'pdpImageHorizontalScrollDepth' : eventType;
        const scrollPercentage = item?.total && Math.round((item?.index / item?.total) * 100);
        prepareGADataAndSendEvent(eventName, user, auction, 'Detail page', { eventLabel: !item?.key ? item?.category : item?.url, index: item?.index, genericLabel: `${(scrollPercentage || scrollPercentage === 0) ? scrollPercentage + '%' : ''}` });
        setIsCarImageOpen(true);
        setActiveCarImagesSection(item);
    };

    const handleGalleryClose = () => {
        setIsCarImageOpen(false);
    };

    const handleSlideChange = (slide) => {
        setThumbsSwiper(null);
        setActiveSlideIndexConnect(slide?.index);
    };
    const gallerySettings = {
        ...settings,
        initialSlide: activeSlideIndex,
        centeredSlides: true,
        onSlideChange: handleSlideChange
    };
    const isCurrentDealerBidLess = !!currentDealerBid && !highestBidForCurrentDealer;
    const isSoldOut = `/${step}` === ROUTE_PATH.ORDERS && isTimePassed(auctionEndTimestamp);
    const thumbsSettings = {
        slidesPerView: 3.9,
        spaceBetween: 2,
        initialSlide: activeSlideIndex,
        pagination: {
            dynamicBullets: true,
            clickable: true,
            dynamicMainBullets: 3
        },
        onSwiper: setThumbsSwiper,
    };
    return (
        <React.Fragment>
            {loading && <div className='mt-10'><SkeletonLoader /></div>}
            {!isApiError &&
                <React.Fragment>
                    {!eligibleForBid && !loading && <Alert severity={TOAST_OPTIONS.WARN}>You are not eligible for bid, please contact your RM</Alert>}
                    <Paper sx={{ position: 'fixed', top: !eligibleForBid && !loading ? 85 : 38, left: 10, zIndex: 9 }} className='bg-transparent shadow-none'>
                        <ArrowBackIcon className='text-primary' onClick={handleBack} />
                    </Paper>
                    {
                        !loading && isValidObject(auction) && <div className='mt-10'>
                            {!isCarImageOpen && <Carousel
                                items={images}
                                pai={pai}
                                height={250}
                                width={270}
                                settings={{ ...gallerySettings }}
                                thumbsSettings={thumbsSettings}
                                isGallery={true}
                                code={isSoldOut && (isOcb ? 'CarWon - OCB' : 'CarWon - Live')}
                                handleImageClick={handleImageOpen.bind(this, 'pdpImageClick')}
                                addThumbnailPadding={false}
                            />}
                            {auction && <AuctionCard isSoldOut={isSoldOut} auction={auction} isSlider={false} isTimer={false} clsName='bg-background' />}
                            <Container maxWidth="sm" className='bg-background py-8 z-10 relative'>
                                <InspectionReport user={user} auction={auction} isMyCarsSection={highestBidForCurrentDealer} handleImageOpen={handleImageOpen.bind(this, 'thumbnailImageClick')} />
                            </Container>
                            {!(isSoldOut || view === 'PD_VIEW') && <BidBottomWrap isOcb={isOcb} ocbV2={ocbV2} isRaiseBid={isCurrentDealerBidLess} endTime={auctionEndTimestamp} handlePlaceBid={handlePlaceBid} canDealerBid={eligibleForBid && !isTimePassed(auctionEndTimestamp)} />}
                        </div>
                    }
                    <BasicModal open={isCarImageOpen} handleClose={handleGalleryClose}>
                        <CarImageModel
                            auction={auction}
                            activeCarImagesSection={activeCarImagesSection}
                            setActiveCarImagesSection={setActiveCarImagesSection}
                            handleClose={handleGalleryClose}
                        />
                    </BasicModal>
                    <BasicModal open={isBidOpen} handleClose={handleBidModalClose}>
                        <BidModal bid={auction} endTime={auctionEndTimestamp} handleClose={handleBidModalClose} handlePlaceBid={handlePlaceBid} />
                    </BasicModal>
                    {!loading && !isValidObject(auction) && <NoDataFound message='No data available.' />}
                    <ConfirmationDialog message={`Are you sure you want to buy this car at AED ${priceFormatter(thresholdBid)}`} okBtnText='Yes' open={isOcbBid} handleClose={setIsOcbBid.bind(null, false)} handleVerified={handleBuyCar} />
                    <UpdateAppModal page={'auction-detail'} />
                </React.Fragment>
            }
            {isApiError && !loading && <NoDataFound />}
        </React.Fragment>
    );
};

AuctionDetail.propTypes = {
    status: PropTypes.string,
    user: PropTypes.object,
    auctionDetail: PropTypes.object,
    getAuctionDetailsConnect: PropTypes.func,
    submitPlaceBidConnect: PropTypes.func,
    setupToastConnect: PropTypes.func,
    setActiveSlideIndexConnect: PropTypes.func
};

export default AuctionDetail;
